import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { formatRelativeAge } from '@/lib/format'
import { now } from '@/lib/clock'
import { ShoppingBag, RefreshCw, Plug, Bot, Activity } from 'lucide-react'

const VISIBLE = 12

/**
 * Skutečné události ze skladu, na rozdíl od původního feedu odvozovaného
 * z rozdílů snapshotů. Díky tomu jde u každé události říct, ze které
 * pobočky přišla.
 */

// usage.<doména>.<akce> -> lidsky čitelný popis
const LABELS_CS: Record<string, string> = {
  'usage.order.created': 'Nová objednávka',
  'usage.order.status_change': 'Změna stavu objednávky',
  'usage.order.consumed': 'Objednávka vyzvednuta',
  'usage.order.payment_method': 'Způsob platby',
  'usage.order.delivery_method': 'Způsob doručení',
  'usage.sync.execute': 'Synchronizace',
  'usage.integration.invocation': 'Volání integrace',
  'usage.outbound_message.sent': 'Odeslaná zpráva',
  'usage.my_mage.model_request': 'AI dotaz',
  'usage.my_mage.model_request.cost': 'AI dotaz — náklad',
  'usage.my_mage.generate_image': 'AI obrázek',
  'usage.my_mage.ingredient_extractor': 'AI rozbor ingrediencí',
  'usage.onboarding_agent.crawl': 'Onboarding — procházení webu',
}
const LABELS_EN: Record<string, string> = {
  'usage.order.created': 'New order',
  'usage.order.status_change': 'Order status changed',
  'usage.order.consumed': 'Order picked up',
  'usage.order.payment_method': 'Payment method',
  'usage.order.delivery_method': 'Delivery method',
  'usage.sync.execute': 'Synchronisation',
  'usage.integration.invocation': 'Integration call',
  'usage.outbound_message.sent': 'Message sent',
  'usage.my_mage.model_request': 'AI request',
  'usage.my_mage.model_request.cost': 'AI request — cost',
  'usage.my_mage.generate_image': 'AI image',
  'usage.my_mage.ingredient_extractor': 'AI ingredient parsing',
  'usage.onboarding_agent.crawl': 'Onboarding — site crawl',
}

/** Pro dosud nepojmenovaný event ať se aspoň neukazuje syrové 'usage.x.y'. */
function prettify(eventName: string): string {
  const parts = eventName.split('.').slice(1)
  if (parts.length === 0) return eventName
  const words = parts.join(' ').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function iconFor(eventName: string) {
  if (eventName.startsWith('usage.order.')) return ShoppingBag
  if (eventName.startsWith('usage.sync.')) return RefreshCw
  if (eventName.startsWith('usage.integration.')) return Plug
  if (eventName.startsWith('usage.my_mage.') || eventName.includes('agent')) return Bot
  return Activity
}

export function RecentActivityFeed() {
  const { t } = useTranslation()
  const locale = useSettingsStore((s) => s.locale)
  const snapshot = useDashboardStore((s) => s.snapshot)
  const live = useDashboardStore((s) => s.liveActivity)
  const cs = locale === 'cs'

  // Živé události jdou navrch, potvrzené ze skladu pod ně. Deduplikace
  // podle event_id, takže se stejná událost neukáže dvakrát.
  // Živá položka si jméno nese z okamžiku příchodu, kdy snapshot ještě
  // nemusel být načtený. Proto ho dohledáváme až tady, přes adresář poboček.
  const dir = snapshot?.venueDirectory ?? {}
  const resolveVenue = (e: { venueName: string; venueId?: string }) =>
    (e.venueId ? dir[e.venueId] : undefined) ?? (e.venueName !== '—' ? e.venueName : '—')

  const merged = [...live, ...(snapshot?.recentActivity ?? [])]
  const seen = new Set<string>()
  const items = merged
    .filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)))
    .slice(0, VISIBLE)
  const labels = cs ? LABELS_CS : LABELS_EN

  return (
    <section className="flex h-full flex-col rounded-3xl border border-border bg-surface-1/70 p-5">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.18em] text-ink-subtle">
          {t('activity.title')}
        </h3>
        <span className="inline-flex items-center gap-1 text-xs text-ink-subtle">
          <span className="size-1.5 animate-pulse rounded-full bg-signal-good" />
          live
        </span>
      </header>

      <div className="relative flex-1 overflow-hidden">
        {items.length === 0 && (
          <div className="grid h-full place-items-center px-4 text-center text-sm text-ink-subtle">
            {t('activity.empty')}
          </div>
        )}
        <ul className="flex flex-col gap-2">
          <AnimatePresence initial={false}>
            {items.map((e) => {
              const Icon = iconFor(e.eventName)
              const base = labels[e.eventName] ?? prettify(e.eventName)
              // U integrace je podstatné, o kterou jde — Wolt a Bolt vypadají
              // v seznamu jinak než vlastní OBJEDNÁME.
              const label = e.integration ? `${base} - ${e.integration}` : base
              const isError = e.status === 'error'
              return (
                <motion.li
                  key={e.id}
                  layout
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex items-center gap-3 rounded-xl border border-border bg-surface-2/50 px-3 py-2"
                >
                  <Icon
                    className="size-4 shrink-0"
                    style={{ color: isError ? 'var(--signal-bad)' : 'var(--brand-primary)' }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-ink">
                      {label}
                      {isError && (
                        <span className="ml-1.5 text-xs text-[var(--signal-bad)]">
                          {cs ? 'chyba' : 'error'}
                        </span>
                      )}
                    </div>
                    {/* Pobočka je to hlavní, co u události chybělo. */}
                    <div className="truncate text-xs text-ink-subtle">
                      {resolveVenue(e)}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-ink-subtle">
                    {formatRelativeAge(e.at, locale, now())}
                  </span>
                </motion.li>
              )
            })}
          </AnimatePresence>
        </ul>
      </div>
    </section>
  )
}
